import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const WISTestimonialsSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');

  const testimonials = [
    {
      id: 1,
      name: "Rajesh Patel",
      company: "Gujarat Aluminum Works",
      role: {
        en: "Warehouse Manager",
        hi: "वेयरहाउस मैनेजर"
      },
      before: {
        accuracy: "85%",
        searchTime: "25 min",
        errors: "15/day"
      },
      after: {
        accuracy: "99.8%",
        searchTime: "3 min",
        errors: "1/week"
      },
      testimonial: {
        en: "WIS transformed our warehouse operations completely. We went from manually tracking 5000+ SKUs to having real-time visibility of every aluminum profile. The barcode scanning feature alone saved us 4 hours daily.",
        hi: "WIS ने हमारे वेयरहाउस संचालन को पूरी तरह से बदल दिया। हम 5000+ SKUs को मैन्युअल रूप से ट्रैक करने से हर एल्यूमीनियम प्रोफाइल की रियल-टाइम दृश्यता तक पहुंच गए। केवल बारकोड स्कैनिंग सुविधा ने हमारा दैनिक 4 घंटे बचाया।"
      },
      improvement: {
        en: "88% reduction in material search time",
        hi: "मैटेरियल खोजने के समय में 88% कमी"
      }
    },
    {
      id: 2,
      name: "Priya Sharma",
      company: "Mumbai Extrusion Ltd",
      role: {
        en: "Operations Director",
        hi: "संचालन निदेशक"
      },
      before: {
        accuracy: "80%",
        fulfillment: "6 hours",
        costs: "₹2.5L/month"
      },
      after: {
        accuracy: "99.9%",
        fulfillment: "2 hours",
        costs: "₹1.5L/month"
      },
      testimonial: {
        en: "The automated reordering feature prevented multiple stockouts that would have cost us lakhs in lost orders. Our inventory carrying costs dropped by 40% while maintaining 100% order fulfillment.",
        hi: "स्वचालित रीऑर्डरिंग सुविधा ने कई स्टॉकआउट को रोका जो हमें खोए हुए ऑर्डर में लाखों का नुकसान होता। हमारी इन्वेंटरी कैरिंग लागत 40% कम हो गई जबकि 100% ऑर्डर पूर्ति बनी रही।"
      },
      improvement: {
        en: "40% reduction in carrying costs",
        hi: "कैरिंग लागत में 40% कमी"
      }
    },
    {
      id: 3,
      name: "Amit Singh",
      company: "Delhi Aluminum Systems",
      role: {
        en: "Plant Manager",
        hi: "प्लांट मैनेजर"
      },
      before: {
        accuracy: "78%",
        efficiency: "65%",
        complaints: "12/month"
      },
      after: {
        accuracy: "99.7%",
        efficiency: "95%",
        complaints: "1/month"
      },
      testimonial: {
        en: "The warehouse layout optimization suggested by WIS increased our picking efficiency by 60%. The 3D visualization helped us redesign our 50,000 sq ft facility for maximum productivity.",
        hi: "WIS द्वारा सुझाए गए वेयरहाउस लेआउट अनुकूलन ने हमारी पिकिंग दक्षता 60% बढ़ा दी। 3D विज़ुअलाइज़ेशन ने हमारी 50,000 वर्ग फीट सुविधा को अधिकतम उत्पादकता के लिए फिर से डिज़ाइन करने में मदद की।"
      },
      improvement: {
        en: "60% increase in picking efficiency",
        hi: "पिकिंग दक्षता में 60% वृद्धि"
      }
    }
  ];

  const t = (text) => text[currentLanguage] || text.en;

  return (
    <section className="py-20 bg-gradient-to-b from-white to-surface-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
            {currentLanguage === 'en' ?'Warehouse Efficiency Success Stories' :'वेयरहाउस दक्षता सफलता की कहानियां'
            }
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            {currentLanguage === 'en' ?'Real results from aluminum manufacturers who transformed their warehouse operations with WIS' :'एल्यूमीनियम निर्माताओं के वास्तविक परिणाम जिन्होंने WIS के साथ अपने वेयरहाउस संचालन को बदल दिया'
            }
          </p>
        </div>

        {/* Testimonials */}
        <div className="space-y-12">
          {testimonials.map((testimonial, index) => (
            <div key={testimonial.id} className={`grid lg:grid-cols-2 gap-8 ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
              {/* Testimonial Content */}
              <div className={`bg-white rounded-2xl shadow-lg p-8 border border-border ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-full flex items-center justify-center font-bold text-xl">
                    {testimonial.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-primary">{testimonial.name}</h3>
                    <p className="text-text-secondary">{testimonial.company}</p>
                    <p className="text-sm text-text-secondary">{t(testimonial.role)}</p>
                  </div>
                </div>

                <blockquote className="text-text-secondary italic mb-6 leading-relaxed">
                  "{t(testimonial.testimonial)}"
                </blockquote>

                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="TrendingUp" size={20} color="var(--color-success)" />
                    <span className="font-semibold text-success">
                      {currentLanguage === 'en' ? 'Key Achievement' : 'मुख्य उपलब्धि'}
                    </span>
                  </div>
                  <p className="text-success font-semibold">{t(testimonial.improvement)}</p>
                </div>
              </div>

              {/* Before/After Metrics */}
              <div className={`bg-gradient-to-br from-surface-50 to-white rounded-2xl shadow-lg p-8 border border-border ${index % 2 === 1 ? 'lg:col-start-1' : ''}`}>
                <h4 className="text-xl font-semibold text-primary mb-6 text-center">
                  {currentLanguage === 'en' ? 'Before vs After WIS' : 'WIS से पहले बनाम बाद'}
                </h4>

                <div className="space-y-6">
                  {Object.keys(testimonial.before).map((metric, metricIndex) => {
                    const metricLabels = {
                      accuracy: currentLanguage === 'en' ? 'Inventory Accuracy' : 'इन्वेंटरी सटीकता',
                      searchTime: currentLanguage === 'en' ? 'Material Search Time' : 'मैटेरियल खोजने का समय',
                      errors: currentLanguage === 'en' ? 'Daily Errors' : 'दैनिक त्रुटियां',
                      fulfillment: currentLanguage === 'en' ? 'Order Fulfillment' : 'ऑर्डर पूर्ति',
                      costs: currentLanguage === 'en' ? 'Monthly Costs' : 'मासिक लागत',
                      efficiency: currentLanguage === 'en' ? 'Picking Efficiency' : 'पिकिंग दक्षता',
                      complaints: currentLanguage === 'en' ? 'Monthly Complaints' : 'मासिक शिकायतें'
                    };

                    return (
                      <div key={metricIndex} className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-sm text-text-secondary mb-1">
                            {metricLabels[metric]}
                          </p>
                          <div className="flex items-center space-x-4">
                            <div className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm">
                              {currentLanguage === 'en' ? 'Before:' : 'पहले:'} {testimonial.before[metric]}
                            </div>
                            <Icon name="ArrowRight" size={16} color="var(--color-text-secondary)" />
                            <div className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold">
                              {currentLanguage === 'en' ? 'After:' : 'बाद:'} {testimonial.after[metric]}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-6 pt-6 border-t border-border">
                  <div className="flex items-center justify-center space-x-2 text-success">
                    <Icon name="CheckCircle" size={20} />
                    <span className="font-semibold">
                      {currentLanguage === 'en' ? 'ROI Achieved in 3 months' : '3 महीने में ROI प्राप्त'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Overall Impact Stats */}
        <div className="mt-16 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl p-8">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold mb-2">
              {currentLanguage === 'en' ? 'Combined Impact Across All Clients' : 'सभी क्लाइंट्स में संयुक्त प्रभाव'}
            </h3>
            <p className="opacity-90">
              {currentLanguage === 'en' ? 'Average improvements achieved by our WIS users' : 'हमारे WIS उपयोगकर्ताओं द्वारा प्राप्त औसत सुधार'}
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold mb-2">99.8%</div>
              <p className="text-sm opacity-90">
                {currentLanguage === 'en' ? 'Average Inventory Accuracy' : 'औसत इन्वेंटरी सटीकता'}
              </p>
            </div>
            <div>
              <div className="text-3xl font-bold mb-2">75%</div>
              <p className="text-sm opacity-90">
                {currentLanguage === 'en' ? 'Reduction in Search Time' : 'खोज समय में कमी'}
              </p>
            </div>
            <div>
              <div className="text-3xl font-bold mb-2">35%</div>
              <p className="text-sm opacity-90">
                {currentLanguage === 'en' ? 'Cost Reduction' : 'लागत में कमी'}
              </p>
            </div>
            <div>
              <div className="text-3xl font-bold mb-2">90%</div>
              <p className="text-sm opacity-90">
                {currentLanguage === 'en' ? 'Error Reduction' : 'त्रुटि में कमी'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WISTestimonialsSection;